/**
 * features/finance/Suppliers.tsx
 * ------------------------------------------------------------------
 * Who the school pays, and into which account.
 *
 * THE BANKING IS THE POINT OF THIS SCREEN. Everything else here — a phone
 * number, an address, a TIN — is a convenience. The account number is the
 * one field that decides where money physically goes, and supplier fraud is
 * almost never a break-in: it is an email on convincing letterhead saying
 * the banking has changed, a helpful person updating the record, and the
 * next genuine invoice paid to a stranger.
 *
 * So the screen is shaped around that. Banking is edited through its own
 * dialog, not the contact form. Changing it demands a reason and clears the
 * verification. Verifying it demands to know HOW. Every change is listed on
 * the supplier, oldest to newest, with who made it.
 *
 * UNVERIFIED BANKING IS SHOWN AS A WARNING ON THE ROW, not buried in a
 * detail view. A bursar scanning the list should see which suppliers could
 * be paid into an account nobody has confirmed, without opening anything.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Truck, Plus, Search, ShieldCheck, ShieldAlert, Landmark, History,
  AlertTriangle, Users, Copy, X,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import {
  SUPPLIER_COLLECTION, isBankVerified, sharedBankAccounts, probableDuplicates,
  bankProblem, supplierProblem,
  type Supplier, type SupplierBank,
} from '@/lib/suppliers';
import {
  saveSupplier, changeBankDetails, verifyBankDetails, setSupplierActive,
} from '@/features/finance/supplierWrite';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const EMPTY_BANK: SupplierBank = { accountName: '', accountNumber: '', bankName: '', branch: '' };

export default function Suppliers() {
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canEdit = perms.canManageSuppliers || perms.canManageFinanceConfig;
  const actor = useMemo(() => ({
    uid: userData?.uid, email: userData?.email, displayName: userData?.displayName,
  }), [userData]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [banking, setBanking] = useState<Supplier | null>(null);
  const [historyFor, setHistoryFor] = useState<Supplier | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, SUPPLIER_COLLECTION)),
      (snap) => {
        setSuppliers(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Supplier)
          .sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return suppliers
      .filter((s) => showInactive || s.active !== false)
      .filter((s) => !needle
        || s.name.toLowerCase().includes(needle)
        || (s.contactPerson ?? '').toLowerCase().includes(needle)
        || (s.bank?.accountNumber ?? '').includes(needle));
  }, [suppliers, q, showInactive]);

  const shared = useMemo(() => sharedBankAccounts(suppliers), [suppliers]);
  const dupes = useMemo(() => probableDuplicates(suppliers), [suppliers]);
  const unverified = useMemo(
    () => suppliers.filter((s) => s.active !== false && s.bank?.accountNumber && !isBankVerified(s)),
    [suppliers],
  );

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Truck}
        title="Suppliers"
        subtitle="Who the school pays, and into which account"
        actions={canEdit ? (
          <button type="button" className={btnPrimary} onClick={() => setEditing('new')}>
            <Plus size={15} /> New supplier
          </button>
        ) : undefined}
      />

      {msg && (
        <div className={`rounded-xl px-4 py-3 text-sm ${msg.ok
          ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatChip icon={Truck} label="Active suppliers" value={String(suppliers.filter((s) => s.active !== false).length)} />
        <StatChip icon={ShieldAlert} label="Banking unverified" value={String(unverified.length)} />
        <StatChip icon={Copy} label="Sharing a bank account" value={String(shared.length)} />
        <StatChip icon={Users} label="Possible duplicates" value={String(dupes.length)} />
      </div>

      {/* THE TWO THINGS WORTH SEEING WITHOUT OPENING ANYTHING. */}
      {shared.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <PanelHeader
            icon={AlertTriangle}
            tone="gold"
            title="Suppliers sharing a bank account"
            subtitle="Sometimes a parent company. Usually one supplier entered twice."
          />
          <div className="p-5 space-y-2 text-sm">
            {shared.map((g) => (
              <p key={g.accountNumber} className="text-gray-700 dark:text-gray-200">
                <span className="font-mono text-xs">{g.accountNumber}</span>
                {' — '}
                {g.suppliers.map((s) => s.name).join(', ')}
              </p>
            ))}
          </div>
        </SoftCard>
      )}

      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Search}
          title="The register"
          right={(
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive
            </label>
          )}
        />
        <div className="p-5 space-y-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, contact or account number"
            className={glassFieldClass('py-2')}
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-4">Supplier</th>
                  <th className="py-2 pr-4">Contact</th>
                  <th className="py-2 pr-4">Bank</th>
                  <th className="py-2 pr-4">Banking</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => {
                  const verified = isBankVerified(s);
                  return (
                    <tr key={s.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4">
                        <span className={s.active === false ? 'text-gray-400 line-through' : ''}>
                          {s.name}
                        </span>
                        {s.active === false && <span className="ml-2 text-xs text-gray-400">inactive</span>}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">
                        {s.contactPerson ?? '—'}
                        {s.contactPhone && <span className="text-xs text-gray-400 ml-2">{s.contactPhone}</span>}
                      </td>
                      <td className="py-2 pr-4">
                        {s.bank?.accountNumber
                          ? (
                            <span className="font-mono text-xs">
                              {s.bank.accountNumber}
                              <span className="text-gray-400 ml-1.5 font-sans">{s.bank.bankName}</span>
                            </span>
                          )
                          : <span className="text-gray-400">none</span>}
                      </td>
                      <td className="py-2 pr-4">
                        {!s.bank?.accountNumber ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : verified ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                            <ShieldCheck size={12} /> Verified
                          </span>
                        ) : (
                          /* ON THE ROW, not in a detail view. A bursar should
                             see which suppliers could be paid into an account
                             nobody has confirmed. */
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            <ShieldAlert size={12} /> Not verified
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {(s.bankHistory?.length ?? 0) > 0 && (
                          <button type="button" className={btnGhost} onClick={() => setHistoryFor(s)}>
                            <History size={14} /> {s.bankHistory!.length}
                          </button>
                        )}
                        {canEdit && (
                          <>
                            <button type="button" className={btnGhost} onClick={() => setBanking(s)}>
                              <Landmark size={14} /> Banking
                            </button>
                            <button type="button" className={btnGhost} onClick={() => setEditing(s)}>
                              Edit
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr><td className="py-4 text-gray-400" colSpan={5}>No suppliers match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </SoftCard>

      {editing && (
        <SupplierDialog
          supplier={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(text) => { setEditing(null); setMsg({ ok: true, text }); }}
          onError={(text) => setMsg({ ok: false, text })}
          onToggleActive={async (s, active) => {
            await setSupplierActive(s.id, active, actor);
            setEditing(null);
            setMsg({ ok: true, text: `${s.name} ${active ? 'reactivated' : 'made inactive'}.` });
          }}
          actor={actor}
        />
      )}

      {banking && (
        <BankingDialog
          supplier={banking}
          onClose={() => setBanking(null)}
          onDone={(text) => { setBanking(null); setMsg({ ok: true, text }); }}
          onError={(text) => setMsg({ ok: false, text })}
          actor={actor}
        />
      )}

      {historyFor && (
        <Dialog title={`Banking history — ${historyFor.name}`} onClose={() => setHistoryFor(null)}>
          <div className="space-y-3">
            {(historyFor.bankHistory ?? []).map((h, i) => (
              <div key={`${h.at}-${i}`} className="text-sm border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                <p className="text-gray-800 dark:text-gray-100">
                  {h.from
                    ? <>Changed from <span className="font-mono text-xs">{h.from.accountNumber}</span> to <span className="font-mono text-xs">{h.to.accountNumber}</span></>
                    : <>Set to <span className="font-mono text-xs">{h.to.accountNumber}</span></>}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {h.at.slice(0, 10)}{h.byName ? ` · ${h.byName}` : ''}
                  {h.to.bankName ? ` · ${h.to.bankName}` : ''}
                </p>
                {h.reason && <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{h.reason}</p>}
              </div>
            ))}
          </div>
        </Dialog>
      )}
    </div>
  );
}

/* -------------------------------- dialogs -------------------------------- */

function Dialog({
  title, onClose, children,
}: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-[2px]" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function SupplierDialog({
  supplier, onClose, onSaved, onError, onToggleActive, actor,
}: {
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: (text: string) => void;
  onError: (text: string) => void;
  onToggleActive: (s: Supplier, active: boolean) => Promise<void>;
  actor: Parameters<typeof saveSupplier>[1];
}) {
  const [form, setForm] = useState({
    name: supplier?.name ?? '',
    contactPerson: supplier?.contactPerson ?? '',
    contactPhone: supplier?.contactPhone ?? '',
    contactEmail: supplier?.contactEmail ?? '',
    address: supplier?.address ?? '',
    tin: supplier?.tin ?? '',
    vatNumber: supplier?.vatNumber ?? '',
    notes: supplier?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const problem = supplierProblem(form);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      await saveSupplier({ ...form, ...(supplier ? { id: supplier.id } : {}) }, actor);
      onSaved(`${form.name} saved.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={supplier ? supplier.name : 'New supplier'} onClose={onClose}>
      <div className="space-y-3">
        {([
          ['name', 'Name', 'Gaborone Stationers (Pty) Ltd'],
          ['contactPerson', 'Contact person', ''],
          ['contactPhone', 'Phone', ''],
          ['contactEmail', 'Email — where remittance advice goes', ''],
          ['address', 'Address', ''],
          ['tin', 'TIN', ''],
          ['vatNumber', 'VAT number', ''],
        ] as const).map(([k, label, placeholder]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{label}</label>
            <input
              value={form[k]}
              placeholder={placeholder}
              onChange={(e) => set(k, e.target.value)}
              className={glassFieldClass('py-2')}
            />
          </div>
        ))}

        {/* Said plainly, because the absence of bank fields here is
            deliberate and would otherwise look like an omission. */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Bank details are changed separately, from the Banking button on the row. Changing
          where money goes is a different decision from changing a phone number.
        </p>

        {problem && <p className="text-sm text-amber-700 dark:text-amber-300">{problem}</p>}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {supplier && (
            <button
              type="button"
              className={btnGhost}
              onClick={() => { void onToggleActive(supplier, supplier.active === false); }}
            >
              {supplier.active === false ? 'Reactivate' : 'Make inactive'}
            </button>
          )}
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={busy || !!problem} onClick={() => { void save(); }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function BankingDialog({
  supplier, onClose, onDone, onError, actor,
}: {
  supplier: Supplier;
  onClose: () => void;
  onDone: (text: string) => void;
  onError: (text: string) => void;
  actor: Parameters<typeof changeBankDetails>[3];
}) {
  const [bank, setBank] = useState<SupplierBank>({ ...EMPTY_BANK, ...(supplier.bank ?? {}) });
  const [reason, setReason] = useState('');
  const [how, setHow] = useState('');
  const [busy, setBusy] = useState(false);
  const verified = isBankVerified(supplier);
  const problem = bankProblem(bank);

  const set = (k: keyof SupplierBank, v: string) => setBank((b) => ({ ...b, [k]: v }));

  async function change() {
    setBusy(true);
    try {
      await changeBankDetails(supplier.id, bank, reason, actor);
      onDone(`Banking updated for ${supplier.name}. It now needs verifying.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function verify() {
    setBusy(true);
    try {
      await verifyBankDetails(supplier.id, how, actor);
      onDone(`Banking confirmed for ${supplier.name}.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Banking — ${supplier.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div className={`rounded-lg px-3 py-2 text-sm flex items-start gap-2 ${verified
          ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200'}`}
        >
          {verified ? <ShieldCheck size={15} className="mt-0.5 shrink-0" /> : <ShieldAlert size={15} className="mt-0.5 shrink-0" />}
          <span>
            {verified
              ? `Confirmed ${supplier.bankVerifiedAt?.slice(0, 10)}`
                + `${supplier.bankVerifiedByName ? ` by ${supplier.bankVerifiedByName}` : ''}`
                + `${supplier.bankVerifiedHow ? ` — ${supplier.bankVerifiedHow}` : ''}.`
              : 'Nobody has confirmed these details. A payment to unverified banking is '
                + 'flagged in Anomaly Review.'}
          </span>
        </div>

        {([
          ['accountName', 'Name on the account'],
          ['accountNumber', 'Account number'],
          ['bankName', 'Bank'],
          ['branch', 'Branch'],
        ] as const).map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{label}</label>
            <input
              value={bank[k] ?? ''}
              onChange={(e) => set(k, e.target.value)}
              className={glassFieldClass('py-2')}
            />
          </div>
        ))}

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Why is this changing? <span className="text-red-500">*</span>
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Letter from the supplier, confirmed by phone with A Sithole"
            className={glassFieldClass('py-2')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Saving new details clears the verification — somebody will have to confirm them again.
          </p>
        </div>

        {problem && <p className="text-sm text-amber-700 dark:text-amber-300">{problem}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={busy || !!problem} onClick={() => { void change(); }}>
            {busy ? 'Saving…' : 'Save banking'}
          </button>
        </div>

        {!verified && supplier.bank?.accountNumber && (
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-2">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
              Confirm the details already on file
            </p>
            <input
              value={how}
              onChange={(e) => setHow(e.target.value)}
              placeholder="How was it confirmed? e.g. Rang the number on the signed contract"
              className={glassFieldClass('py-2')}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Confirm against something other than the message that asked for the change —
              a number from the contract, a cancelled cheque, a letter collected in person.
            </p>
            <div className="flex justify-end">
              <button type="button" className={btnPrimary} disabled={busy} onClick={() => { void verify(); }}>
                <ShieldCheck size={14} /> Mark as verified
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
